import AccountModel              from '@models/account/AccountModel';
import ClientRequestStatusModel  from '@models/account/ClientRequestStatusModel';
import { ClientRequestStatusId } from '@models/account/ClientRequestStatusModel';
import ClientRequestTypeModel    from '@models/account/ClientRequestTypeModel';
import UserModel                 from '@models/account/UserModel';
import doc                       from '@widesk-core/decorators/doc';
import model                     from '@widesk-core/decorators/model';
import AccountServiceModel       from '@/models/AccountServiceModel';
import TimestampAble             from '@widesk-core/models/traits/TimestampAble';
import Blamable                  from '@/models/traits/Blamable';
import dayjs                     from 'dayjs';

@model.urnResource('client_request')
@doc.path('/client_requests/{?id}')
export default class ClientRequestModel extends Blamable(TimestampAble(AccountServiceModel)) {
	private declare _tsFilters: {
		'account': id;
		'clientRequestStatus': id;
		'clientRequestType': id;
		'dueDate[after]': string | Date;
		'dueDate[before]': string | Date;
		'effectiveDueDate[after]': string | Date;
		'effectiveDueDate[before]': string | Date;
		'effectiveDueDate[strictly_after]': string | Date;
		'effectiveDueDate[strictly_before]': string | Date;
		'email': string;
		'extendedDueDate[after]': string | Date;
		'extendedDueDate[before]': string | Date;
		'id': id;
		'search': string;
		'subject': string;
		'user': id;
	};

	private declare _tsSorts: {
		'createdAt': string;
		'dueDate': string;
		'effectiveDueDate': string;
		'extendedDueDate': string;
		'id': string;
		'updatedAt': string;
	};

	@doc.model(AccountModel) declare account: AccountModel;
	@doc.model(ClientRequestStatusModel) declare clientRequestStatus: ClientRequestStatusModel;
	@doc.model(ClientRequestTypeModel) declare clientRequestType: ClientRequestTypeModel;
	@doc.string declare email: string;
	@doc.string declare firstname: string;
	@doc.string declare lastname: string;
	@doc.string declare locale: string;
	@doc.string declare subject: string;
	@doc.string declare token: string;
	@doc.model(UserModel) declare user: UserModel;

	public get modelLabel(): string {
		return `#${this.id} ${this.subject}`;
	}

	public get requesterFullName(): string {
		return [this.firstname, this.lastname].filter(Boolean).join(' ');
	}

	public get requesterUrn(): Urn | undefined {
		return this.get('user.@urn');
	}

	public get isOpen(): boolean {
		const statusId = String(this.get('clientRequestStatus.id'));

		return statusId === ClientRequestStatusId.submitted || statusId === ClientRequestStatusId.inProgress;
	}

	public get isExtended(): boolean {
		return !!this.get('extendedDueDate');
	}

	public get effectiveDueDate(): Dayjs | undefined {
		const value = this.get('effectiveDueDate');

		return value ? dayjs(value) : undefined;
	}

	public get isPastDue(): boolean {
		return !!this.effectiveDueDate?.isBefore(dayjs());
	}

	public get isOverdue(): boolean {
		return this.isPastDue && this.isOpen;
	}

	public get canBeExtended(): boolean {
		const dueDate = this.get('dueDate');

		return this.clientRequestType.isGdpr
			&& this.isOpen
			&& !this.isExtended
			&& !!dueDate
			&& dayjs(dueDate).isAfter(dayjs());
	}
}
